import { Box, Flex } from '@chakra-ui/react';
import { datadogRum } from '@datadog/browser-rum';
import PQueue from 'p-queue';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownItem,
  ModalBody,
  ModalFooterDefault,
  ModalHeaderDefault,
  SavedListEdit,
  SavedListEditCreatorChangeHandler,
  SearchInput,
} from 'ui-components';
import { trackEvent } from '~/core/utils/trackEvent';
import {
  useGetSavedListCreatorsSearch,
  useUpdateSaveListCreators,
} from '~/hooks/api/useSavedLists';
import { useUpdateCreatorNote } from '~/hooks/api/useUpdateCreatorNote';

import { STEP } from '../SavedListsModal.constants';
import { useSavedListsModalProvider } from '../SavedListsModal.context';
import { useSavedListsBodyHeight } from '../SavedListsModal.hooks';
import { useSavedListStateStore } from '../SavedListsModal.store';
import { SavedListSubmitError } from '../SavedListsModal.types';
import { EmptyResults } from './EmptyResults';
import { ErrorNotification } from './ErrorNotification';

const updateListQueue = new PQueue({ concurrency: 1, autoStart: false });
const updateListCreatorQueue = new PQueue({ concurrency: 1, autoStart: false });

export const StepTwoAddNotes: React.FC = () => {
  const [errors, setErrors] = useState<SavedListSubmitError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const { lists, updateListCreator } = useSavedListStateStore();
  const { creators, setStep, onClose, onSuccess } =
    useSavedListsModalProvider();
  const { mutateAsync: mutateCreatorNote } = useUpdateCreatorNote({
    disableSnackbar: true,
  });
  const { mutateAsync: mutateLists } = useUpdateSaveListCreators({
    disableInvalidation: true,
  });

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const [activeListId, setActiveListId] = useState<string>(
    Object.keys(lists)[0],
  );

  const activeList = lists[activeListId];

  const {
    data: activeListCreators,
    isFetching,
    isLoading,
    isRefetching,
  } = useGetSavedListCreatorsSearch({
    creatorIds: creators.map(({ id }) => id),
    id: activeListId,
  });

  const handleSubmit = useCallback(async () => {
    const creatorUpdateRequests: any[] = [];
    const listUpdateRequests: any[] = [];
    const updatedErrors: SavedListSubmitError[] = [];

    setIsSubmitting(true);

    Object.entries(lists).forEach(([id, list]) => {
      // Add list mutation to queue
      listUpdateRequests.push(() =>
        mutateLists(
          { id, addCreatorIds: list.creators.map(({ id }) => id) },
          {
            onError(error) {
              if (error instanceof Error) {
                datadogRum.addError(error, {
                  message: 'Error ocurred while updating list',
                  listId: id,
                });
                return updatedErrors.push({
                  message: error.message,
                  listId: id,
                  type: 'list',
                });
              }

              datadogRum.addError(
                new Error('Error ocurred while updating list'),
                {
                  listId: id,
                },
              );

              return updatedErrors.push({
                message: `Error ocurred while updating list`,
                listId: id,
                type: 'list',
              });
            },
          },
        ),
      );
      // Add creator notes mutation to queue
      list.creators.forEach(({ dirty, id: creatorId, note: notes }) => {
        if (notes?.length || dirty) {
          creatorUpdateRequests.push(() =>
            mutateCreatorNote(
              { creatorId, id, notes },
              {
                onError(error) {
                  if (error instanceof Error) {
                    datadogRum.addError(error, {
                      message: 'Error ocurred while updating creator note',
                      creatorId,
                    });
                    return updatedErrors.push({
                      creatorId,
                      listId: id,
                      message: error.message,
                      type: 'note',
                    });
                  }

                  datadogRum.addError(
                    new Error('Error ocurred while updating creator note'),
                    {
                      creatorId,
                    },
                  );

                  return updatedErrors.push({
                    creatorId,
                    listId: id,
                    message: `Error ocurred while updating creator note`,
                    type: 'note',
                  });
                },
              },
            ),
          );
        }
      });
    });

    // Track event for adding to list
    trackEvent(
      listUpdateRequests.length === 1
        ? 'foam-add-to-single-list'
        : 'foam-add-to-multiple-lists',
    );

    // Track event for adding to list with note
    trackEvent(
      creatorUpdateRequests.length
        ? 'foam-add-to-list-with-note'
        : 'foam-add-to-list-skip-note',
    );

    updateListQueue.addAll(listUpdateRequests);
    updateListQueue.start();
    await updateListQueue.onIdle();

    updateListCreatorQueue.addAll(creatorUpdateRequests);
    updateListCreatorQueue.start();
    await updateListCreatorQueue.onIdle();

    if (!updatedErrors.length) {
      onSuccess && onSuccess(lists);
      return onClose();
    }

    setErrors(updatedErrors);

    setIsSubmitting(false);
  }, [mutateLists, lists, mutateCreatorNote, onClose, onSuccess]);

  const handleCreatorChange: SavedListEditCreatorChangeHandler = useCallback(
    (list, updatedCreator) => {
      updateListCreator(list.id, updatedCreator);
    },
    [activeList, updateListCreator],
  );

  useEffect(() => {
    const listIds = Object.keys(lists);
    if (!activeList && listIds.length) {
      setActiveListId(listIds[0]);
    }
  }, [activeList, lists]);

  const maxListHeight = useSavedListsBodyHeight({
    deps: [errors],
    modalBodyRef,
    toolbarRef,
  });

  const filtered = useMemo(() => {
    const creators = (activeList?.creators ?? []).map((creator) => {
      const activeCreator = activeListCreators?.find(
        ({ creatorId }) => creatorId === creator.id,
      );

      if (activeCreator?.notes) {
        const updatedCreator = {
          ...creator,
          defaultNote: activeCreator.notes,
          isInList: true,
        };

        if (!updatedCreator.note) {
          delete updatedCreator.note;
        }

        return updatedCreator;
      }

      return creator;
    });

    if (query) {
      return creators.filter(({ name, searchable = [] }) => {
        const re = new RegExp(query, 'gi');
        return name.match(re) || searchable.toString().match(re);
      });
    }

    return creators;
  }, [activeListCreators, activeList?.creators, query]);

  if (!activeList || !Object.keys(lists).length) {
    return null;
  }

  return (
    <>
      <ModalHeaderDefault title="Add notes (optional)" />

      <ModalBody ref={modalBodyRef}>
        <Box
          opacity={isSubmitting ? 0.5 : undefined}
          pointerEvents={isSubmitting ? 'none' : 'auto'}
        >
          <Flex
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            marginBottom={6}
            ref={toolbarRef}
          >
            <ErrorNotification errors={errors} setErrors={setErrors} />

            {Object.keys(lists).length > 1 && (
              <>
                <Dropdown>
                  <DropdownButton>{activeList.name}</DropdownButton>
                  <DropdownContent>
                    {Object.entries(lists).map(([id, list]) => (
                      <DropdownItem
                        key={id}
                        isActive={id === activeListId}
                        onClick={() => setActiveListId(id)}
                      >
                        {list.name}
                      </DropdownItem>
                    ))}
                  </DropdownContent>
                </Dropdown>
              </>
            )}

            <SearchInput
              defaultValue={query}
              marginLeft="auto"
              onChange={(value) => setQuery(value ?? '')}
              inputProps={{
                onClear: () => setQuery(''),
                placeholder: 'Search Talent',
              }}
            />
          </Flex>

          {query.length && !filtered.length ? (
            <EmptyResults
              height={maxListHeight.current}
              onQueryReset={() => setQuery('')}
              query={query}
            />
          ) : (
            <SavedListEdit
              creators={filtered}
              isLoading={isFetching || isLoading || isRefetching}
              list={{ id: activeListId, name: activeList.name }}
              onCreatorChange={handleCreatorChange}
              styles={{ height: maxListHeight.current }}
            />
          )}
        </Box>
      </ModalBody>

      <ModalFooterDefault
        primary={{
          ariaLabel: 'Add Talent',
          isLoading: isSubmitting,
          onClick: handleSubmit,
          title: 'Add Talent',
          trackId: 'foam-add-to-list-final-step',
        }}
        secondary={{
          ariaLabel: 'Back',
          isDisabled: isSubmitting,
          onClick: () => setStep(STEP.step1),
          title: 'Back',
          trackId: 'foam-add-to-list-back-button',
        }}
      />
    </>
  );
};
